import React, { useId, useState } from 'react'
import { DateTime } from 'luxon'
import css from './ActivityChart.module.css'
import LoadingSkeleton from '../../utility/LoadingSceleton/LoadingSkeleton'
import { GymMonthlyActivity } from '../../../types/types'

type Props = {
  activity: GymMonthlyActivity | undefined
  isLoading: boolean
}

type Month = GymMonthlyActivity['months'][number]

/**
 * The Members page sidebar: three charts over the same six completed
 * months.
 * - Members: stacked bars, tracked (bottom) and viewed only (top), so a
 *   bar's height is the month's total. Copy says "total", not "active".
 * - Tracking rate: a line, tracked out of members who viewed.
 * - PR rate: a line, hit a PR out of members who tracked a lift or
 *   benchmark (the only workouts that can produce one).
 * Hover or focus a month for the numbers. Plain elements and one SVG line,
 * no chart library: six points per chart don't need one.
 */
const ActivityChart = ({ activity, isLoading }: Props) => {
  const months = activity?.months ?? []

  return (
    <div className={css['chart']}>
      <header className={css['header']}>
        <h3 style={{ margin: 0 }}>Last 6 months</h3>
      </header>
      {!isLoading && !months.length ? (
        <section>
          <p className={css['empty']}>Monthly activity is unavailable</p>
        </section>
      ) : (
        <div className={css['cards']}>
          <MembersCard months={months} isLoading={isLoading} />
          <TrackingRateCard months={months} isLoading={isLoading} />
          <PRRateCard months={months} isLoading={isLoading} />
        </div>
      )}
    </div>
  )
}

// Formulas

const totalOf = (month: Month) => month.tracked + (month.viewedOnly ?? 0)

/** Tracked / viewed (viewing includes tracking), 0–1. Null without views */
const trackingRateOf = (month: Month) =>
  month.viewedOnly === null || !totalOf(month)
    ? null
    : month.tracked / totalOf(month)

/**
 * Members with a PR / members who tracked a workout that can produce one
 * (lifts, or benchmarks; other workouts never do), 0–1. Null if none did
 */
const prRateOf = (month: Month) =>
  month.prEligibleMembers ? month.prMembers / month.prEligibleMembers : null

const formatPercent = (rate: number) => `${Math.round(rate * 100)}%`

type Tick = { ratio: number; label: string }

/** Rates run 0–100%, so a line's height reads as the share */
const RATE_TICKS: Tick[] = [0, 0.5, 1].map((ratio) => ({
  ratio,
  label: formatPercent(ratio),
}))

/**
 * Round grid steps for counts (1, 2 or 5 × 10ⁿ), at most four above zero.
 * The top tick is the scale's maximum, so bars line up with the grid
 */
const countTicks = (max: number): Tick[] => {
  const magnitude = 10 ** Math.floor(Math.log10(Math.max(1, max)))
  // The smallest round step that needs four lines or fewer, e.g. 50 for 133
  const step =
    [0.1, 0.2, 0.5, 1, 2, 5, 10]
      .map((m) => m * magnitude)
      .filter((s) => s >= 1)
      .find((s) => max / s <= 4) ?? 10 * magnitude
  const top = Math.max(step, Math.ceil(max / step) * step)
  return Array.from({ length: top / step + 1 }, (_, index) => ({
    ratio: (index * step) / top,
    label: String(index * step),
  }))
}

// Cards

type CardProps = { months: Month[]; isLoading: boolean }

const MembersCard = ({ months, isLoading }: CardProps) => {
  const ticks = countTicks(Math.max(0, ...months.map(totalOf)))
  const top = Number(ticks[ticks.length - 1].label)

  return (
    <Card
      title="Members"
      subtitle="How many members viewed or tracked workouts"
      legend={[
        { label: 'Tracked', swatch: 'tracked' },
        { label: 'Viewed only', swatch: 'viewed' },
      ]}
    >
      <MonthPlot
        months={months}
        isLoading={isLoading}
        layout="bars"
        ticks={ticks}
        label="Members who viewed or tracked a workout, per month"
        describe={(month) =>
          `${totalOf(month)} members, ${month.tracked} tracked, ${
            month.viewedOnly ?? 'unknown'
          } viewed only`
        }
        tooltipRows={(month) => [
          { label: 'Tracked', value: month.tracked, swatch: 'tracked' },
          {
            label: 'Viewed only',
            value: month.viewedOnly ?? '—',
            swatch: 'viewed',
          },
          { label: 'Total', value: totalOf(month), isTotal: true },
        ]}
        heightOf={(month) => totalOf(month) / top}
        renderMarks={(month) => {
          const ratio = totalOf(month) / top
          return (
            <>
              <span
                className={css['value-label']}
                style={{ bottom: `calc(${ratio * 100}% + 4px)` }}
              >
                {totalOf(month)}
              </span>
              <span
                className={css['bar']}
                style={{ height: `${ratio * 100}%` }}
              >
                <span
                  className={css['viewed']}
                  style={{ flexGrow: month.viewedOnly ?? 0 }}
                />
                <span
                  className={css['tracked']}
                  style={{ flexGrow: month.tracked }}
                />
              </span>
            </>
          )
        }}
      />
    </Card>
  )
}

const TrackingRateCard = ({ months, isLoading }: CardProps) => (
  <RateCard
    months={months}
    isLoading={isLoading}
    title="Tracking rate"
    tone="green"
    subtitle="How many members who viewed workouts also tracked one"
    label="Tracking rate: members who tracked, of members who viewed, per month"
    rateOf={trackingRateOf}
    describe={(month, rate) =>
      `${formatPercent(rate)}, ${month.tracked} of ${totalOf(month)}`
    }
    tooltipRows={(month, rate) => [
      { label: 'Viewed', value: totalOf(month) },
      { label: 'Tracked', value: month.tracked },
      {
        label: 'Tracking rate',
        value: rate === null ? '—' : formatPercent(rate),
        isTotal: true,
      },
    ]}
  />
)

const PRRateCard = ({ months, isLoading }: CardProps) => (
  <RateCard
    months={months}
    isLoading={isLoading}
    title="PR rate"
    tone="pr"
    subtitle="How many members who lifted set a new PR"
    label="PR rate: members who hit a PR, of members who tracked a lift or benchmark, per month"
    rateOf={prRateOf}
    describe={(month, rate) =>
      `${formatPercent(rate)}, ${month.prMembers} of ${month.prEligibleMembers}`
    }
    tooltipRows={(month, rate) => [
      { label: 'Tracked a lift or benchmark', value: month.prEligibleMembers },
      { label: 'Hit a PR', value: month.prMembers },
      { label: 'PRs', value: month.prs },
      {
        label: 'PR rate',
        value: rate === null ? '—' : formatPercent(rate),
        isTotal: true,
      },
    ]}
  />
)

/** A rate per month as an edge-to-edge line with labeled points, 0–100% */
const RateCard = ({
  months,
  isLoading,
  title,
  tone,
  subtitle,
  label,
  rateOf,
  describe,
  tooltipRows,
}: CardProps & {
  title: string
  /** Line color: green, or PR yellow like the app's PR badges */
  tone: 'green' | 'pr'
  subtitle: string
  label: string
  rateOf: (month: Month) => number | null
  describe: (month: Month, rate: number) => string
  tooltipRows: (month: Month, rate: number | null) => TooltipRow[]
}) => {
  // SVG coordinates in a 100 × 100 box stretched over the plot
  const coordinates = months.flatMap((month, index) => {
    const rate = rateOf(month)
    return rate === null
      ? []
      : [[pointX(index, months.length) * 100, (1 - rate) * 100]]
  })
  const points = coordinates.map(([x, y]) => `${x},${y}`).join(' ')
  // The line closed down to the baseline, for the fill under it
  const area = coordinates.length
    ? `${coordinates[0][0]},100 ${points} ${
        coordinates[coordinates.length - 1][0]
      },100`
    : ''
  // Unique per card: SVG gradient ids are global to the page
  const gradientID = `rate-fill-${useId()}`

  return (
    <Card title={title} subtitle={subtitle}>
      <MonthPlot
        months={months}
        isLoading={isLoading}
        layout="points"
        tone={tone}
        ticks={RATE_TICKS}
        label={label}
        describe={(month) => {
          const rate = rateOf(month)
          return rate === null ? 'no data' : describe(month, rate)
        }}
        tooltipRows={(month) => tooltipRows(month, rateOf(month))}
        heightOf={(month) => rateOf(month) ?? 0}
        background={
          <svg
            className={css['line']}
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden
          >
            <defs>
              <linearGradient id={gradientID} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="currentColor" stopOpacity="0.18" />
                <stop offset="1" stopColor="currentColor" stopOpacity="0" />
              </linearGradient>
            </defs>
            <polygon points={area} fill={`url(#${gradientID})`} />
            <polyline points={points} />
          </svg>
        }
        renderMarks={(month) => {
          const rate = rateOf(month)
          if (rate === null) return null
          return (
            <>
              <span
                className={css['value-label']}
                style={{ bottom: `calc(${rate * 100}% + 8px)` }}
              >
                {formatPercent(rate)}
              </span>
              <span
                className={css['dot']}
                style={{ bottom: `${rate * 100}%` }}
              />
            </>
          )
        }}
      />
    </Card>
  )
}

type Swatch = 'tracked' | 'viewed'

/** White card: title, one-line subtitle, the plot, then an optional legend */
const Card = ({
  title,
  subtitle,
  legend,
  children,
}: {
  title: string
  subtitle: string
  legend?: { label: string; swatch: Swatch }[]
  children: React.ReactNode
}) => (
  <section className={css['card']}>
    <div className={css['card-header']}>
      <h4 className={css['card-title']}>{title}</h4>
      <p className={css['card-subtitle']}>{subtitle}</p>
    </div>
    {children}
    {legend && (
      <div className={css['legend']} aria-hidden>
        {legend.map((item) => (
          <span key={item.label}>
            <i className={css[item.swatch]} />
            {item.label}
          </span>
        ))}
      </div>
    )}
  </section>
)

// Plot

type TooltipRow = {
  label: string
  value: number | string
  swatch?: Swatch
  isTotal?: boolean
}

/**
 * Where month `index` sits across the plot, 0–1. Points run edge to edge
 * (first month at the left edge, last at the right); bars sit centered in
 * equal bands
 */
const pointX = (index: number, count: number) =>
  count > 1 ? index / (count - 1) : 0.5

const bandOf = (index: number, count: number, layout: 'bars' | 'points') => {
  if (layout === 'bars') {
    return { center: (index + 0.5) / count, width: 1 / count }
  }
  // Each point owns the space halfway to its neighbours
  return { center: pointX(index, count), width: 1 / Math.max(1, count - 1) }
}

type MonthPlotProps = {
  months: Month[]
  isLoading: boolean
  layout: 'bars' | 'points'
  /** Color of lines and dots */
  tone?: 'green' | 'pr'
  /** Horizontal grid lines with y-axis labels, 0–1 of the plot height */
  ticks: Tick[]
  /** For screen readers, e.g. "PR rate, per month" */
  label: string
  /** The month's numbers for screen readers, after the month name */
  describe: (month: Month) => string
  tooltipRows: (month: Month) => TooltipRow[]
  /** Height of the month's top mark, 0–1, so the tooltip sits just above */
  heightOf: (month: Month) => number
  /**
   * A month's marks, positioned with `bottom`/`height` in % of the plot's
   * height and centered in the month's band
   */
  renderMarks: (month: Month) => React.ReactNode
  /** Drawn over the whole plot behind the marks, e.g. a line */
  background?: React.ReactNode
}

// Shown while loading, in place of the real marks
const SKELETON_RATIOS = [0.55, 0.7, 0.6, 0.8, 0.65, 0.5]

/**
 * A y-axis, then the plot: horizontal grid lines at `ticks`, vertical ones
 * at each month, the marks, and month names below. Every mark is placed in
 * % of the same frame, so bars, dots, the line and the grid always agree.
 * Hovering or focusing a month shows a tooltip beside it and fades the rest.
 */
const MonthPlot = ({
  months,
  isLoading,
  layout,
  tone = 'green',
  ticks,
  label,
  describe,
  tooltipRows,
  heightOf,
  renderMarks,
  background,
}: MonthPlotProps) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const count = isLoading ? SKELETON_RATIOS.length : months.length

  return (
    <div className={`${css['plot']} ${css[`chart-${tone}`]}`}>
      <div className={css['axis']} aria-hidden>
        {ticks.map((tick) => (
          <span
            key={tick.ratio}
            className={css['tick-label']}
            style={{ bottom: `${tick.ratio * 100}%` }}
          >
            {tick.label}
          </span>
        ))}
      </div>

      <div className={css['frame']}>
        <div className={css['grid']} aria-hidden>
          {ticks.map((tick) => (
            <span
              key={tick.ratio}
              className={css['grid-row']}
              style={{ bottom: `${tick.ratio * 100}%` }}
            />
          ))}
          {Array.from({ length: count }, (_, index) => (
            <span
              key={index}
              className={css['grid-column']}
              style={{ left: `${bandOf(index, count, layout).center * 100}%` }}
            />
          ))}
        </div>

        {isLoading ? (
          <div aria-busy="true" aria-label="Loading">
            {SKELETON_RATIOS.map((ratio, index) => (
              <Band key={index} band={bandOf(index, count, 'bars')}>
                <div className={css['marks']}>
                  <LoadingSkeleton
                    type="block"
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: '50%',
                      width: 'calc(100% - 12px)',
                      height: `${ratio * 100}%`,
                      borderRadius: '4px 4px 0 0',
                      transform: 'translateX(-50%)',
                    }}
                  />
                </div>
              </Band>
            ))}
          </div>
        ) : (
          <>
            {background}
            <ul
              className={`${css['bands']} ${
                activeIndex !== null ? css['has-active'] : ''
              }`}
              aria-label={label}
            >
              {months.map((month, index) => {
                const isActive = index === activeIndex
                // Edge-to-edge points: the first and last labels anchor
                // inward, so they stay clear of the y-axis and the card edge
                const edge =
                  layout !== 'points'
                    ? ''
                    : index === 0
                    ? css['edge-start']
                    : index === count - 1
                    ? css['edge-end']
                    : ''
                return (
                  <Band
                    key={month.start}
                    as="li"
                    band={bandOf(index, count, layout)}
                    className={`${isActive ? css['active'] : ''} ${edge}`}
                  >
                    <div className={css['marks']}>{renderMarks(month)}</div>
                    <span className={css['month']} aria-hidden>
                      {DateTime.fromMillis(month.start).toFormat('LLL')}
                    </span>
                    <button
                      type="button"
                      className={css['target']}
                      aria-label={`${monthTitle(month)}: ${describe(month)}`}
                      onMouseEnter={() => setActiveIndex(index)}
                      onMouseLeave={() => setActiveIndex(null)}
                      onFocus={() => setActiveIndex(index)}
                      onBlur={() => setActiveIndex(null)}
                    />
                    {isActive && (
                      <Tooltip
                        month={month}
                        rows={tooltipRows(month)}
                        height={heightOf(month)}
                        // Above the month, centered; the first and last
                        // anchor inward so they stay inside the card
                        align={
                          index === 0
                            ? 'start'
                            : index === count - 1
                            ? 'end'
                            : 'center'
                        }
                      />
                    )}
                  </Band>
                )
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  )
}

/** A month's slice of the plot: full height, centered on its position */
const Band = ({
  band,
  as: Element = 'div',
  className = '',
  children,
}: {
  band: { center: number; width: number }
  as?: 'div' | 'li'
  className?: string
  children: React.ReactNode
}) => (
  <Element
    className={`${css['band']} ${className}`}
    style={{
      left: `${(band.center - band.width / 2) * 100}%`,
      width: `${band.width * 100}%`,
    }}
  >
    {children}
  </Element>
)

const Tooltip = ({
  month,
  rows,
  height,
  align,
}: {
  month: Month
  rows: TooltipRow[]
  height: number
  align: 'start' | 'center' | 'end'
}) => (
  <div
    className={`${css['tooltip']} ${css[`align-${align}`]}`}
    // Just above the bar or point and its value label
    style={{
      bottom: `calc(var(--month-room) + var(--frame-height) * ${height} + 28px)`,
    }}
    role="tooltip"
  >
    <div className={css['tooltip-title']}>{monthTitle(month)}</div>
    <dl>
      {rows.map((row) => (
        <React.Fragment key={row.label}>
          <dt className={row.isTotal ? css['tooltip-total'] : ''}>
            {row.swatch && <i className={css[row.swatch]} />}
            {row.label}
          </dt>
          <dd className={row.isTotal ? css['tooltip-total'] : ''}>
            {row.value}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  </div>
)

/** E.g. "June 2026" */
const monthTitle = (month: Month) =>
  DateTime.fromMillis(month.start).toFormat('LLLL yyyy')

export default ActivityChart
